import React, { useRef, useEffect, useState } from 'react';
import { 
  Paper, 
  Typography, 
  Grid, 
  Divider, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  Box,
  Dialog,
  DialogContent,
  DialogActions
} from '@mui/material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';

// Styles optimisés pour le PDF au format A4
const pdfStyles = {
  paper: {
    padding: '15px',
    width: '210mm',  // Largeur exacte A4
    minHeight: '297mm', // Hauteur exacte A4
    margin: '0 auto',
    backgroundColor: '#ffffff',
    fontSize: '0.85rem',
    boxSizing: 'border-box',
    pageBreakInside: 'avoid',
    overflowX: 'hidden',
    boxShadow: 'none',
    border: '1px solid #e0e0e0'
  },
  header: {
    textAlign: 'center',
    marginBottom: '12px'
  },
  section: {
    marginBottom: '12px'
  },
  footer: {
    marginTop: '15px',
    paddingTop: '8px',
    borderTop: '1px solid #ccc',
    textAlign: 'center'
  },
  signature: {
    marginTop: '20px',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px'
  },
  logo: {
    maxHeight: '65px',
    maxWidth: '140px',
    objectFit: 'contain',
    marginBottom: '8px'
  },
  tableContainer: {
    marginBottom: '12px',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  table: {
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed'
  },
  tableCell: {
    padding: '5px 10px',
    fontSize: '0.75rem',
    lineHeight: '1.2',
    borderBottom: '1px solid #e0e0e0'
  },
  tableHeadCell: {
    padding: '5px 10px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #e0e0e0'
  },
  title: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    marginBottom: '5px',
    textAlign: 'center'
  },
  subtitle: {
    fontSize: '1rem',
    fontWeight: 'medium',
    marginBottom: '5px',
    textAlign: 'center'
  },
  sectionTitle: {
    fontSize: '0.9rem',
    fontWeight: 'bold',
    marginTop: '10px',
    marginBottom: '5px'
  }
};

const PrintableServiceSheet = ({ serviceSheetId, establishmentId, open, onClose }) => {
  const [serviceSheet, setServiceSheet] = useState(null);
  const [establishmentInfo, setEstablishmentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef(null);

  // Charger les données de la fiche de service et de l'établissement
  useEffect(() => {
    const fetchData = async () => {
      if (!serviceSheetId || !establishmentId) return;
      
      try {
        setLoading(true);
        
        // Récupérer les données de la fiche de service
        const serviceSheetDoc = await getDoc(doc(db, 'establishments', establishmentId, 'serviceSheets', serviceSheetId));
        if (serviceSheetDoc.exists()) {
          const sheetData = serviceSheetDoc.data();
          
          // S'assurer que le champ heuresFractionnees existe
          // Pour les fiches existantes qui n'ont pas ce champ
          if (sheetData.heuresFractionnees === undefined) {
            sheetData.heuresFractionnees = 0;
          }
          
          setServiceSheet({
            id: serviceSheetDoc.id,
            ...sheetData
          });
        }
        
        // Récupérer les informations de l'établissement
        const establishmentDoc = await getDoc(doc(db, 'establishments', establishmentId));
        if (establishmentDoc.exists()) {
          setEstablishmentInfo(establishmentDoc.data());
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setLoading(false);
      }
    };
    
    if (open) {
      fetchData();
    }
  }, [serviceSheetId, establishmentId, open]);

  // Fonction pour optimiser les éléments du PDF avant génération
  const optimizePdfElements = (clonedDoc) => {
    // Optimiser le conteneur principal
    const container = clonedDoc.querySelector('div[class*="MuiPaper-root"]');
    if (container) {
      container.style.width = '210mm';
      container.style.minHeight = '297mm';
      container.style.boxSizing = 'border-box';
      container.style.padding = '15mm 10mm';
      container.style.margin = '0';
      container.style.backgroundColor = '#ffffff';
      container.style.boxShadow = 'none';
      container.style.border = '1px solid #e0e0e0';
    }
    
    // Améliorer la qualité des images
    const images = clonedDoc.getElementsByTagName('img');
    for (let i = 0; i < images.length; i++) {
      if (images[i].src.startsWith('data:')) continue;
      images[i].style.maxWidth = '100%';
      images[i].style.objectFit = 'contain';
      images[i].style.imageRendering = 'crisp-edges';
    }
    
    // Optimiser tous les tableaux pour l'impression
    const tableContainers = clonedDoc.querySelectorAll('div[class*="MuiTableContainer-root"]');
    for (let i = 0; i < tableContainers.length; i++) {
      tableContainers[i].style.marginBottom = '12px';
      tableContainers[i].style.border = '1px solid #e0e0e0';
      tableContainers[i].style.borderRadius = '4px';
      tableContainers[i].style.overflow = 'hidden';
      tableContainers[i].style.pageBreakInside = 'avoid';
    }
    
    // Optimiser les tableaux
    const tables = clonedDoc.getElementsByTagName('table');
    for (let i = 0; i < tables.length; i++) {
      tables[i].style.width = '100%';
      tables[i].style.borderCollapse = 'collapse';
      tables[i].style.tableLayout = 'fixed';
    }
    
    // Optimiser les cellules des tableaux
    const cells = clonedDoc.getElementsByTagName('td');
    for (let i = 0; i < cells.length; i++) {
      cells[i].style.padding = '4px 8px';
      cells[i].style.fontSize = '9pt';
      cells[i].style.lineHeight = '1.2';
      cells[i].style.borderBottom = '1px solid #e0e0e0';
      cells[i].style.wordBreak = 'break-word';
    }
    
    // Optimiser les en-têtes de tableaux
    const headerCells = clonedDoc.querySelectorAll('th, td[class*="MuiTableCell-head"]');
    for (let i = 0; i < headerCells.length; i++) {
      headerCells[i].style.padding = '4px 8px';
      headerCells[i].style.fontSize = '9pt';
      headerCells[i].style.fontWeight = 'bold';
      headerCells[i].style.backgroundColor = '#f5f5f5';
      headerCells[i].style.borderBottom = '1px solid #e0e0e0';
    }
    
    // Optimiser les titres
    const titles = clonedDoc.querySelectorAll('h5, h6');
    for (let i = 0; i < titles.length; i++) {
      titles[i].style.pageBreakAfter = 'avoid';
      titles[i].style.pageBreakBefore = 'auto';
      titles[i].style.fontSize = titles[i].tagName === 'H5' ? '16pt' : '14pt';
      titles[i].style.fontWeight = 'bold';
      titles[i].style.textAlign = 'center';
      titles[i].style.margin = '5px 0';
    }
    
    // Optimiser les sous-titres
    const subtitles = clonedDoc.querySelectorAll('[class*="MuiTypography-subtitle1"]');
    for (let i = 0; i < subtitles.length; i++) {
      subtitles[i].style.fontSize = '12pt';
      subtitles[i].style.fontWeight = 'bold';
      subtitles[i].style.marginTop = '10px';
      subtitles[i].style.marginBottom = '5px';
      subtitles[i].style.pageBreakAfter = 'avoid';
    }
    
    // Optimiser les dividers
    const dividers = clonedDoc.querySelectorAll('hr, [class*="MuiDivider-root"]');
    for (let i = 0; i < dividers.length; i++) {
      dividers[i].style.margin = '8px 0';
      dividers[i].style.borderColor = '#e0e0e0';
    }
    
    // Optimiser la zone de signature
    const signatureBox = clonedDoc.querySelector('div[style*="border: 1px solid #ccc"]');
    if (signatureBox) {
      signatureBox.style.marginTop = '15px';
      signatureBox.style.padding = '10px';
      signatureBox.style.border = '1px solid #e0e0e0';
      signatureBox.style.borderRadius = '4px';
      signatureBox.style.pageBreakInside = 'avoid';
    }
  };

  // Fonction pour générer le PDF instantanément avec un rendu optimal pour A4
  const generatePDF = () => {
    if (!contentRef.current) return;
    
    try {
      // Créer un nouveau document PDF au format A4
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
        precision: 2,
        hotfixes: ['px_scaling']
      });
      
      // Paramètres optimisés pour un rendu parfait sur A4
      const options = {
        scale: 2, // Qualité optimale pour impression
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        imageTimeout: 0,
        width: contentRef.current.offsetWidth,
        height: contentRef.current.offsetHeight,
        scrollX: 0,
        scrollY: 0,
        removeContainer: true,
        letterRendering: true,
        // Optimiser les polices
        fontCallback: (family, size) => {
          return size < 10 ? 10 : size; // Assurer une taille minimale de police
        },
        // Optimiser le contenu avant la génération
        onclone: (clonedDoc) => {
          // Appliquer toutes les optimisations
          optimizePdfElements(clonedDoc);
        }
      };
      
      // Générer le nom du fichier
      const fileName = `Fiche_Service_${serviceSheet.annee}_${serviceSheet.annee + 1}.pdf`;
      
      // Utiliser html2canvas avec des paramètres optimisés
      html2canvas(contentRef.current, options).then(canvas => {
        // Qualité d'image optimale pour impression
        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        const imgWidth = 210; // Largeur A4 en mm
        const pageHeight = 297; // Hauteur A4 en mm
        const imgHeight = canvas.height * imgWidth / canvas.width;
        
        // Calculer les marges pour centrer le contenu
        const margin = 0;
        const contentWidth = 210 - (margin * 2);
        const contentHeight = Math.min(imgHeight, pageHeight - (margin * 2));
        
        // Ajouter l'image au PDF avec des marges appropriées
        pdf.addImage(imgData, 'JPEG', margin, margin, contentWidth, contentHeight);
        
        // Télécharger le PDF immédiatement
        pdf.save(fileName);
      }).catch(error => {
        console.error('Erreur lors de la génération du PDF:', error);
      });
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
    }
  };

  // Calculs pour la fiche de service
  const calculateServiceSheetData = () => {
    if (!serviceSheet) return {};
    
    // Calcul des heures travaillées en présence d'élèves
    const heuresPresenceEleves = serviceSheet.semainesTravaillees * serviceSheet.heuresParSemaine || 0;
    
    // Calcul des heures travaillées hors présence d'élèves (permanences)
    const heuresPermanences = serviceSheet.heuresDues - heuresPresenceEleves;
    
    // Calcul du nombre de jours travaillés (8h par jour)
    const joursPresenceEleves = Math.round(heuresPresenceEleves / 8);
    // Arrondir au nombre inférieur pour les permanences
    const joursPermanences = Math.floor(heuresPermanences / 8);
    
    // Calcul du total des jours travaillés
    const totalJoursTravailles = joursPresenceEleves + joursPermanences;
    
    // Calcul des heures totales
    const totalHeuresTravaillees = heuresPresenceEleves + heuresPermanences;
    
    return {
      heuresPresenceEleves,
      heuresPermanences,
      joursPresenceEleves,
      joursPermanences,
      totalJoursTravailles,
      totalHeuresTravaillees
    };
  };

  const {
    heuresPresenceEleves,
    heuresPermanences,
    joursPresenceEleves,
    joursPermanences,
    totalJoursTravailles,
    totalHeuresTravaillees
  } = calculateServiceSheetData();

  if (loading || !serviceSheet) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Typography>Chargement des données...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="outlined" onClick={() => onClose()} startIcon={<CloseIcon />}>Fermer</Button>
          <Box>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<PictureAsPdfIcon />} 
              onClick={generatePDF}
            >
              Générer PDF
            </Button>
          </Box>
        </Box>
        
        <Paper sx={pdfStyles.paper} ref={contentRef}>
          {/* En-tête avec logo et nom de l'établissement */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              {establishmentInfo?.logo && (
                <img 
                  src={establishmentInfo.logo} 
                  alt="Logo établissement" 
                  style={pdfStyles.logo} 
                />
              )}
            </Box>
            <Box sx={{ textAlign: 'center', flexGrow: 1 }}>
              <Typography variant="h5" gutterBottom>
                {establishmentInfo?.name || 'Établissement scolaire'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {establishmentInfo?.address || ''}
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="h5" align="center" sx={{ ...pdfStyles.title, mb: 0.5 }}>
            FICHE DE SERVICE ANNUEL
          </Typography>
          
          <Typography variant="h6" align="center" sx={{ ...pdfStyles.subtitle, mb: 0.5 }}>
            Année scolaire {serviceSheet.annee}-{serviceSheet.annee + 1}
          </Typography>
          
          {serviceSheet.service && (
            <Typography variant="subtitle1" align="center" sx={{ ...pdfStyles.subtitle, mb: 1, fontWeight: 'medium' }}>
              Service : {serviceSheet.service}
            </Typography>
          )}
          
          <Divider sx={{ my: 1 }} />
          
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                <strong>Date d'édition :</strong> {format(new Date(), 'dd/MM/yyyy', { locale: fr })}
              </Typography>
            </Grid>
          </Grid>
          
          {/* Informations essentielles uniquement */}
          <Typography variant="subtitle1" sx={{ ...pdfStyles.sectionTitle, mt: 1, mb: 0.5 }}>
            OBLIGATIONS HORAIRES
          </Typography>
          
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small" padding="none">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={pdfStyles.tableHeadCell}><strong>Description</strong></TableCell>
                  <TableCell align="right" sx={pdfStyles.tableHeadCell}><strong>Valeur</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={pdfStyles.tableCell}>Heures de base annuelles</TableCell>
                  <TableCell align="right" sx={pdfStyles.tableCell}>{serviceSheet.heuresBase} h</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={pdfStyles.tableCell}>Semaines travaillées</TableCell>
                  <TableCell align="right" sx={pdfStyles.tableCell}>{serviceSheet.semainesTravaillees} semaines</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={pdfStyles.tableCell}>Heures par semaine</TableCell>
                  <TableCell align="right" sx={pdfStyles.tableCell}>{serviceSheet.heuresParSemaine} h</TableCell>
                </TableRow>
                {serviceSheet.service && (
                  <TableRow>
                    <TableCell sx={pdfStyles.tableCell}>Service</TableCell>
                    <TableCell align="right" sx={pdfStyles.tableCell}>{serviceSheet.service}</TableCell>
                  </TableRow>
                )}
                {serviceSheet.personnel?.horaireDecale && (
                  <>
                    <TableRow>
                      <TableCell sx={pdfStyles.tableCell}><strong>Horaire décalé</strong></TableCell>
                      <TableCell align="right" sx={pdfStyles.tableCell}>Oui</TableCell>
                    </TableRow>
                    {serviceSheet.personnel?.observationHoraire && (
                      <TableRow>
                        <TableCell sx={pdfStyles.tableCell}>Observation</TableCell>
                        <TableCell align="right" sx={pdfStyles.tableCell}>{serviceSheet.personnel.observationHoraire}</TableCell>
                      </TableRow>
                    )}
                  </>
                )}
                <TableRow>
                  <TableCell sx={pdfStyles.tableCell}>Jours fériés</TableCell>
                  <TableCell align="right" sx={pdfStyles.tableCell}>
                    {`${(() => {
                      // Utiliser heuresFeriesParJour s'il existe, sinon utiliser 8 par défaut
                      const heuresParJour = serviceSheet.heuresFeriesParJour || 8;
                      const joursFeries = serviceSheet.heuresFeries / heuresParJour;
                      // Arrondir au nombre supérieur si la partie décimale est supérieure à 0.5
                      const joursArrondis = Math.floor(joursFeries) + (joursFeries % 1 > 0.5 ? 1 : Math.ceil(joursFeries % 1));
                      return `${joursArrondis} jours (${serviceSheet.heuresFeries} h, ${heuresParJour}h/j)`;
                    })()}`}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={pdfStyles.tableCell}>Heures fractionnées</TableCell>
                  <TableCell align="right" sx={pdfStyles.tableCell}>{serviceSheet.heuresFractionnees || 0} h</TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={pdfStyles.tableHeadCell}><strong>Heures dues</strong></TableCell>
                  <TableCell align="right" sx={pdfStyles.tableHeadCell}><strong>{serviceSheet.heuresDues} h</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          
          <Typography variant="subtitle1" sx={{ ...pdfStyles.sectionTitle, mt: 1, mb: 0.5 }}>
            RÉPARTITION DES JOURS TRAVAILLÉS
          </Typography>
          
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small" padding="none">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={pdfStyles.tableHeadCell}><strong>Type</strong></TableCell>
                  <TableCell align="right" sx={pdfStyles.tableHeadCell}><strong>Jours</strong></TableCell>
                  <TableCell align="right" sx={pdfStyles.tableHeadCell}><strong>Heures</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={pdfStyles.tableCell}>Jours travaillés en présence d'élèves</TableCell>
                  <TableCell align="right" sx={pdfStyles.tableCell}>{joursPresenceEleves}</TableCell>
                  <TableCell align="right" sx={pdfStyles.tableCell}>{heuresPresenceEleves} h</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={pdfStyles.tableCell}>Jours travaillés hors présence d'élèves (permanences)</TableCell>
                  <TableCell align="right" sx={pdfStyles.tableCell}>{joursPermanences}</TableCell>
                  <TableCell align="right" sx={pdfStyles.tableCell}>{heuresPermanences} h</TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={pdfStyles.tableHeadCell}><strong>Total</strong></TableCell>
                  <TableCell align="right" sx={pdfStyles.tableHeadCell}><strong>{totalJoursTravailles}</strong></TableCell>
                  <TableCell align="right" sx={pdfStyles.tableHeadCell}><strong>{totalHeuresTravaillees} h</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Périodes de vacances et permanences */}
          <Typography variant="subtitle1" sx={{ ...pdfStyles.sectionTitle, mt: 1, mb: 0.5 }}>
            PÉRIODES DE VACANCES ET PERMANENCES
          </Typography>
          
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small" padding="none">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={pdfStyles.tableHeadCell}><strong>Période</strong></TableCell>
                  <TableCell sx={pdfStyles.tableHeadCell}><strong>Dates</strong></TableCell>
                  <TableCell align="right" sx={pdfStyles.tableHeadCell}><strong>Jours</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {serviceSheet.vacancesPeriodes && serviceSheet.vacancesPeriodes.length > 0 ? (
                  serviceSheet.vacancesPeriodes.map((periode, index) => (
                    <TableRow key={index}>
                      <TableCell sx={pdfStyles.tableCell}>{periode.nom || `Période ${index + 1}`}</TableCell>
                      <TableCell sx={pdfStyles.tableCell}>
                        {periode.debut && periode.fin ? 
                          `${format(new Date(periode.debut.seconds * 1000), 'dd/MM/yy', { locale: fr })} - ${format(new Date(periode.fin.seconds * 1000), 'dd/MM/yy', { locale: fr })}` : 
                          'Non spécifié'}
                      </TableCell>
                      <TableCell align="right" sx={pdfStyles.tableCell}>{periode.permanences || 0}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={pdfStyles.tableCell}>Aucune période de vacances sélectionnée</TableCell>
                  </TableRow>
                )}
                {serviceSheet.permanences && serviceSheet.permanences.length > 0 && (
                  <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                    <TableCell sx={pdfStyles.tableCell}><strong>Permanences hors vacances</strong></TableCell>
                    <TableCell sx={pdfStyles.tableCell}>-</TableCell>
                    <TableCell align="right" sx={pdfStyles.tableCell}>{serviceSheet.permanences.reduce((total, p) => total + (p.jours || 0), 0)}</TableCell>
                  </TableRow>
                )}
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell colSpan={2} sx={pdfStyles.tableHeadCell}><strong>Total des jours de permanence</strong></TableCell>
                  <TableCell align="right" sx={pdfStyles.tableHeadCell}><strong>{joursPermanences}</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Zone de signature */}
          <Box sx={{ border: '1px solid #ccc', borderRadius: '4px', mt: 1, p: 1, pageBreakInside: 'avoid' }}>
            <Grid container spacing={0}>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Le gestionnaire du lycée</strong>
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {establishmentInfo?.gestionnaire || 'Nom du gestionnaire'}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption">Signature :</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Date</strong>
                </Typography>
                <Typography variant="body2">
                  {format(new Date(), 'dd/MM/yyyy', { locale: fr })}
                </Typography>
              </Grid>
            </Grid>
          </Box>
          
          <Box sx={{ mt: 2, mb: 1, pt: 1, borderTop: '1px solid #ccc' }}>
            <Typography variant="caption" color="textSecondary" align="center">
              Document généré le {format(new Date(), 'dd/MM/yyyy à HH:mm', { locale: fr })}
            </Typography>
          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default PrintableServiceSheet;
